/**
 * ReactionForm — log an allergic reaction (POST /api/reactions/log).
 *
 * Reused in two places: the Product Scanner (pre-filled with the scanned
 * product) and the standalone Reactions screen (free-text product name).
 *
 * Props:
 *   product?  { id, name }   — pre-fills product context when logging from a scan
 *   onLogged()               — called after a successful save
 *   onCancel()               — called when the user dismisses the form
 */

import React, { useState } from 'react';
import Alert from '../common/Alert';
import useAPI from '../../hooks/useAPI';

// Mirrors backend VALID_SYMPTOMS with human-readable labels.
export const SYMPTOM_OPTIONS = [
  { id: 'itching', label: 'Itching' },
  { id: 'hives', label: 'Hives' },
  { id: 'rash', label: 'Rash' },
  { id: 'swelling_lips', label: 'Swelling — lips' },
  { id: 'swelling_tongue', label: 'Swelling — tongue' },
  { id: 'swelling_throat', label: 'Swelling — throat' },
  { id: 'nausea', label: 'Nausea' },
  { id: 'vomiting', label: 'Vomiting' },
  { id: 'diarrhea', label: 'Diarrhea' },
  { id: 'abdominal_pain', label: 'Abdominal pain' },
  { id: 'wheezing', label: 'Wheezing' },
  { id: 'difficulty_breathing', label: 'Difficulty breathing' },
  { id: 'anaphylaxis', label: 'Anaphylaxis' },
  { id: 'dizziness', label: 'Dizziness' },
  { id: 'loss_of_consciousness', label: 'Loss of consciousness' },
  { id: 'other', label: 'Other' },
];

const severityLabel = (n) => (n >= 8 ? 'Severe' : n >= 5 ? 'Moderate' : 'Mild');

// Local datetime as 'YYYY-MM-DDTHH:MM' for a datetime-local input default.
const nowLocal = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

export default function ReactionForm({ product, onLogged, onCancel }) {
  const { post } = useAPI();
  const [symptoms, setSymptoms] = useState([]);
  const [severity, setSeverity] = useState(5);
  const [occurredAt, setOccurredAt] = useState(nowLocal());
  const [productName, setProductName] = useState(product?.name || '');
  const [epi, setEpi] = useState(false);
  const [called911, setCalled911] = useState(false);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const toggleSymptom = (id) =>
    setSymptoms((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (symptoms.length === 0) {
      setError('Select at least one symptom.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await post('/api/reactions/log', {
        product_id: product?.id || null,
        product_name: productName.trim() || null,
        severity_score: severity,
        symptoms,
        epigen_administered: epi,
        emergency_services_called: called911,
        notes: notes.trim() || null,
        reaction_occurred_at: new Date(occurredAt).toISOString(),
      });
      onLogged?.();
    } catch (err) {
      setError(err.message || 'Could not log the reaction');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="reaction-form" onSubmit={handleSubmit}>
      <h3 className="reaction-form-title">Log a reaction</h3>

      {product?.name ? (
        <p className="reaction-form-product">
          Product: <strong>{product.name}</strong>
        </p>
      ) : (
        <>
          <label className="input-label" htmlFor="rf-product">Food / product (optional)</label>
          <input
            id="rf-product"
            className="input"
            value={productName}
            onChange={(e) => setProductName(e.target.value)}
            placeholder="What did you eat?"
          />
        </>
      )}

      <label className="input-label">Symptoms</label>
      <div className="reaction-symptoms">
        {SYMPTOM_OPTIONS.map((s) => (
          <label key={s.id} className="checkbox-label">
            <input
              type="checkbox"
              checked={symptoms.includes(s.id)}
              onChange={() => toggleSymptom(s.id)}
            />
            <span>{s.label}</span>
          </label>
        ))}
      </div>

      <label className="input-label" htmlFor="rf-sev">
        Severity: <strong>{severity}/10 — {severityLabel(severity)}</strong>
      </label>
      <input
        id="rf-sev"
        type="range"
        min="1"
        max="10"
        value={severity}
        onChange={(e) => setSeverity(parseInt(e.target.value, 10))}
        className="reaction-range"
      />

      <label className="input-label" htmlFor="rf-when">When did it happen?</label>
      <input
        id="rf-when"
        type="datetime-local"
        className="input"
        value={occurredAt}
        max={nowLocal()}
        onChange={(e) => setOccurredAt(e.target.value)}
      />

      <div className="reaction-flags">
        <label className="checkbox-label">
          <input type="checkbox" checked={epi} onChange={(e) => setEpi(e.target.checked)} />
          <span>Epinephrine (EpiPen) used</span>
        </label>
        <label className="checkbox-label">
          <input type="checkbox" checked={called911} onChange={(e) => setCalled911(e.target.checked)} />
          <span>Called emergency services</span>
        </label>
      </div>

      <label className="input-label" htmlFor="rf-notes">Notes (optional)</label>
      <textarea
        id="rf-notes"
        className="input reaction-notes"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Anything useful for you or your allergist…"
        rows={3}
      />

      {error && <Alert variant="error" title="Error" message={error} />}

      <div className="reaction-form-actions">
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving…' : 'Log reaction'}
        </button>
      </div>
    </form>
  );
}
